import { FileText, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { Button } from '@/components/ui/button';
import { DataTable, type Column } from '@/components/ui/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { FilterPills } from '@/components/ui/filter-pills';
import { KpiCard } from '@/components/ui/kpi-card';
import { Pagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { INVOICE_TONES, useAdminInvoices } from '@/hooks/invoices';
import { formatDay } from '@/lib/dates';
import { NewInvoiceDialog } from '@/pages/admin/invoices/InvoiceDialogs';
import { formatCents } from '@shared/money';
import {
  INVOICE_STATUS_LABELS,
  type AdminInvoiceListItem,
  type InvoiceStatusValue,
} from '@shared/schemas/invoice';
import { DEFAULT_PAGE_SIZE, RECORD_SORTS } from '@shared/schemas/pagination';
import { SortSelect } from '@/components/ui/sort-select';
import type { RecordSort } from '@shared/schemas/pagination';

/*
 * /admin/invoices — ROADMAP.md §12.
 *
 * **Overdue is a filter and a marker, never a status.** The rows still say
 * Sent, because that is what they are; the red word beside the due date is
 * derived on the server from the calendar and the payments, and storing it
 * anywhere is the §12.4 mistake this project wrote down before starting.
 *
 * The three cards are real figures and zero renders as `$0.00` (P1, P10) — an
 * office with nothing outstanding is a fact, not an absence.
 */

const STATUS_FILTERS: { value: InvoiceStatusValue | 'overdue' | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: INVOICE_STATUS_LABELS.draft },
  { value: 'sent', label: INVOICE_STATUS_LABELS.sent },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: INVOICE_STATUS_LABELS.paid },
  { value: 'void', label: INVOICE_STATUS_LABELS.void },
];

/** `?status=overdue` from a link, or `all`. Anything unrecognised is ignored. */
function statusFromUrl(params: URLSearchParams): InvoiceStatusValue | 'overdue' | 'all' {
  const asked = params.get('status');
  return STATUS_FILTERS.some((filter) => filter.value === asked)
    ? (asked as InvoiceStatusValue | 'overdue' | 'all')
    : 'all';
}

export function InvoicesPage() {
  const navigate = useNavigate();

  /*
   * **The filter can arrive in the URL**, so the dashboard's "Invoices overdue"
   * row can land on the overdue list rather than on everything. Read once as
   * the initial value rather than kept in sync with the address bar: the pills
   * below are the control after that, and mirroring both directions would make
   * clicking a pill a navigation.
   */
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<InvoiceStatusValue | 'overdue' | 'all'>(() =>
    statusFromUrl(searchParams),
  );
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<RecordSort>('newest');
  const [creating, setCreating] = useState(false);

  const invoices = useAdminInvoices({
    sort,
    page,
    status,
    search: search === '' ? undefined : search,
  });
  const totals = invoices.data?.totals;

  const columns: Column<AdminInvoiceListItem>[] = [
    {
      key: 'number',
      header: 'Invoice',
      render: (row) => (
        <Link
          to={`/admin/invoices/${row.id}`}
          className="font-medium text-bark-700 underline underline-offset-2"
        >
          {row.invoiceNumber}
        </Link>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) => row.clientName,
    },
    {
      key: 'issued',
      header: 'Issued',
      render: (row) =>
        row.issueDate === null ? (
          <span className="text-ink-500">Not yet</span>
        ) : (
          <span className="whitespace-nowrap">{formatDay(row.issueDate)}</span>
        ),
    },
    {
      key: 'due',
      header: 'Due',
      render: (row) => (
        <span className="whitespace-nowrap">
          {row.dueDate === null ? <span className="text-ink-500">—</span> : formatDay(row.dueDate)}
          {row.isOverdue ? (
            <span className="ml-2 rounded-sm bg-red-100 px-1.5 py-0.5 text-meta font-medium text-red-800">
              Overdue
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'right',
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums">{formatCents(row.totalCents)}</span>
      ),
    },
    {
      key: 'balance',
      header: 'Balance',
      align: 'right',
      // Zero is $0.00 — a settled invoice's balance is a fact, not a dash.
      render: (row) => (
        <span className="whitespace-nowrap tabular-nums">{formatCents(row.balanceCents)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge label={row.statusLabel} tone={INVOICE_TONES[row.status]} />,
    },
  ];

  return (
    <AdminPage
      title="Invoices"
      description="What the business has billed, what has been paid, and what is still owed."
      actions={
        <Button
          onClick={() => {
            setCreating(true);
          }}
        >
          <Plus aria-hidden="true" className="size-4" />
          New invoice
        </Button>
      }
    >
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label="Outstanding"
          value={totals?.outstandingCents ?? null}
          format={formatCents}
          isLoading={invoices.isPending}
        />
        <KpiCard
          label="Overdue"
          value={totals?.overdueCents ?? null}
          format={formatCents}
          isLoading={invoices.isPending}
        />
        <KpiCard label="Drafts" value={totals?.draftCount ?? null} isLoading={invoices.isPending} />
      </div>

      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <FilterPills
          label="Filter invoices by status"
          options={STATUS_FILTERS}
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
        />
        <SearchInput
          value={search}
          onChange={(next) => {
            setSearch(next);
            setPage(1);
          }}
          placeholder="Search by number or client"
          label="Search invoices"
        />
      </div>

      {/* The order control. A select rather than clickable headers —

          the list becomes cards below 768px and a card has no headers. */}

      <div className="mb-3 flex justify-end">
        <SortSelect
          label="Sort invoices"

          options={RECORD_SORTS}

          value={sort}

          onChange={(next) => {
            setSort(next);

            // Page 1, or you are on page 3 of a differently ordered list.

            setPage(1);
          }}
        />
      </div>

      <DataTable
        caption="Invoices, newest first"
        columns={columns}
        rows={invoices.data?.data ?? []}
        rowKey={(row) => row.id}
        // On a phone the whole card is the link — see DataTable's `rowHref`.
        rowHref={(row) => `/admin/invoices/${row.id}`}
        isLoading={invoices.isPending}
        error={invoices.error}
        onRetry={() => {
          void invoices.refetch();
        }}
        empty={
          <EmptyState
            icon={FileText}
            title={
              status === 'overdue'
                ? 'Nothing is overdue.'
                : status === 'all'
                  ? 'No invoices yet.'
                  : 'No invoices match this filter.'
            }
            hint="Build one from completed jobs — pick the client, tick the cleans, and the lines price themselves from the rate card."
          />
        }
      />

      <Pagination
        page={page}
        pageSize={invoices.data?.pageSize ?? DEFAULT_PAGE_SIZE}
        total={invoices.data?.total ?? 0}
        onPageChange={setPage}
        itemNoun="invoices"
      />

      <NewInvoiceDialog
        open={creating}
        onOpenChange={setCreating}
        onCreated={(id) => void navigate(`/admin/invoices/${id}`)}
      />
    </AdminPage>
  );
}
